import React, { useState, useRef, useEffect, useCallback } from "react";
import { speakText } from "../utils/speech";
import confetti from "canvas-confetti";
import GameSummary from "./GameSummary";
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import HomeIcon from '@mui/icons-material/Home';
import DiamondIcon from '@mui/icons-material/Diamond';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export default function SpellingPractice({ words, kidName, onLogout, onExit }) {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [userInput, setUserInput] = useState("");
    const [result, setResult] = useState("");
    const [score, setScore] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [showSummary, setShowSummary] = useState(false);
    const [tries, setTries] = useState(0);
    const [locked, setLocked] = useState(false);
    const [streak, setStreak] = useState(0);
    const [totalPoints, setTotalPoints] = useState(0);
    const [scorePopup, setScorePopup] = useState(null);
    const [maxStreak, setMaxStreak] = useState(0);
    const [personalBest, setPersonalBest] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showWrongAnswer, setShowWrongAnswer] = useState(false);
    const [showHint, setShowHint] = useState(false);
    const [hintLevel, setHintLevel] = useState(0);

    const inputRef = useRef(null);

    const getPerfectScore = () => {
        let pts = 0;
        for (let i = 0; i < words.length; i++) {
            let m = i >= 10 ? 5 : i >= 7 ? 4 : i >= 5 ? 3 : i >= 3 ? 2 : 1;
            pts += 30 * m;
        }
        return pts;
    };

    useEffect(() => {
        const init = () => window.speechSynthesis.getVoices();
        init();
        window.speechSynthesis.onvoiceschanged = init;
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, []);

    useEffect(() => {
        const key = `personalBest_${kidName}_${words.map(w => w.word).join('-').substring(0, 50)}`;
        const saved = localStorage.getItem(key);
        if (saved) setPersonalBest(parseInt(saved));
    }, [kidName, words]);

    const getStreakMultiplier = () =>
        streak >= 10 ? 5 : streak >= 7 ? 4 : streak >= 5 ? 3 : streak >= 3 ? 2 : 1;

    const calculatePoints = (attemptNumber) => {
        const base = attemptNumber === 1 ? 30 : attemptNumber === 2 ? 20 : 10;
        return base * getStreakMultiplier();
    };

    const speakWord = useCallback((index = currentWordIndex) => {
        if (!words[index]) return;
        speakText(words[index].word, 'en', 0.9);
        setTimeout(() => inputRef.current?.focus(), 200);
    }, [words, currentWordIndex]);

    useEffect(() => {
        speakWord();
        setTimeout(() => inputRef.current?.focus(), 300);
        setShowHint(false);
        setHintLevel(0);
    }, [currentWordIndex, speakWord]);

    const playCorrectSound = () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [[523.25, 0, 'square'], [659.25, 0.15, 'square'], [783.99, 0.3, 'square'], [1046.50, 0.45, 'square']].forEach(([f, d]) => {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.type = 'square'; o.frequency.setValueAtTime(f, ctx.currentTime + d);
            g.gain.setValueAtTime(0.2, ctx.currentTime + d);
            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + d + 0.15);
            o.connect(g); g.connect(ctx.destination);
            o.start(ctx.currentTime + d); o.stop(ctx.currentTime + d + 0.15);
        });
    };

    const playWrongSound = () => {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [[400, 0, 'sawtooth'], [350, 0.1, 'sawtooth'], [250, 0.2, 'sawtooth']].forEach(([f, d, t]) => {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.type = t; o.frequency.setValueAtTime(f, ctx.currentTime + d);
            g.gain.setValueAtTime(0.2, ctx.currentTime + d);
            g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + d + 0.2);
            o.connect(g); g.connect(ctx.destination);
            o.start(ctx.currentTime + d); o.stop(ctx.currentTime + d + 0.2);
        });
    };

    const getWeekYearKey = () => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        now.setDate(now.getDate() + 4 - (now.getDay() || 7));
        const yearStart = new Date(now.getFullYear(), 0, 1);
        const weekNo = Math.ceil((((now - yearStart) / 86400000) + 1) / 7);
        return `${now.getFullYear()}_W${weekNo}`;
    };

    const saveLeaderboard = (finalScore, finalAttempts) => {
        if (!kidName) return;
        const key = `leaderboard_${kidName}_${getWeekYearKey()}`;
        const existing = JSON.parse(localStorage.getItem(key)) || { score: 0, attempts: 0 };
        if (finalScore > existing.score) localStorage.setItem(key, JSON.stringify({ score: finalScore, attempts: finalAttempts }));
    };

    const savePersonalBest = (points) => {
        if (!kidName) return false;
        const key = `personalBest_${kidName}_${words.map(w => w.word).join('-').substring(0, 50)}`;
        const existing = parseInt(localStorage.getItem(key)) || 0;
        if (points > existing) { localStorage.setItem(key, points.toString()); setPersonalBest(points); return true; }
        return false;
    };

    const getLeaderboard = () =>
        Object.keys(localStorage).filter(k => k.startsWith("leaderboard_")).map(key => {
            const parts = key.split("_");
            return { name: parts[1], weekYear: parts[2], ...JSON.parse(localStorage.getItem(key)) };
        }).sort((a, b) => b.score - a.score);

    const handleSubmit = () => {
        if (locked || words.length === 0 || !userInput.trim() || showWrongAnswer || showSuccess) return;
        const correctWord = words[currentWordIndex].word.trim().toLowerCase();
        const userWord = userInput.trim().toLowerCase();
        const nextAttempts = attempts + 1;
        const nextTries = tries + 1;

        if (userWord === correctWord) {
            const nextScore = score + 1;
            const earnedPoints = calculatePoints(nextTries);
            const nextTotalPoints = totalPoints + earnedPoints;
            const nextStreak = streak + 1;
            setShowSuccess(true);
            setScorePopup({ points: earnedPoints, x: 50, y: 30 });
            setTimeout(() => setScorePopup(null), 2000);
            setResult("Correct!");
            setScore(nextScore);
            setTotalPoints(nextTotalPoints);
            setStreak(nextStreak);
            setMaxStreak(Math.max(maxStreak, nextStreak));
            setAttempts(nextAttempts);
            setTries(0);
            playCorrectSound();
            const animationEnd = Date.now() + 2000;
            const frame = () => {
                confetti({ particleCount: 3, angle: 55 + Math.random() * 70, spread: 50 + Math.random() * 20, origin: { x: Math.random(), y: Math.random() - 0.2 }, colors: ['#4caf50', '#ffd700', '#2196f3'], gravity: 0.6 });
                if (Date.now() < animationEnd) requestAnimationFrame(frame);
            };
            frame();
            if (currentWordIndex + 1 < words.length) {
                setTimeout(() => { setShowSuccess(false); setCurrentWordIndex(currentWordIndex + 1); setUserInput(""); setResult(""); setLocked(false); }, 2000);
            } else {
                setTimeout(() => {
                    setShowSuccess(false);
                    saveLeaderboard(nextScore, nextAttempts);
                    if (savePersonalBest(nextTotalPoints)) setTimeout(() => confetti({ particleCount: 200, spread: 100, origin: { y: 0.6 } }), 500);
                    setShowSummary(true);
                }, 2000);
            }
        } else {
            playWrongSound();
            setAttempts(nextAttempts);
            setTries(nextTries);
            setStreak(0);
            setShowWrongAnswer(true);
            if (nextTries >= 3) {
                setResult(`The correct spelling is: "${correctWord}"`);
                setLocked(true);
                setTimeout(() => {
                    setShowWrongAnswer(false);
                    if (currentWordIndex + 1 < words.length) { setCurrentWordIndex(currentWordIndex + 1); setUserInput(""); setResult(""); setTries(0); setLocked(false); }
                    else { saveLeaderboard(score, nextAttempts); setShowSummary(true); }
                }, 2000);
            } else {
                setResult(`Try again! (${nextTries}/3)`);
                setTimeout(() => setShowWrongAnswer(false), 2000);
            }
        }
        setTimeout(() => inputRef.current?.focus(), 100);
    };

    const getHintText = () => {
        const word = words[currentWordIndex].word;
        if (hintLevel === 1) return `Hint: This word has ${word.length} letter${word.length !== 1 ? 's' : ''}`;
        if (hintLevel === 2) return `Hint: It starts with "${word[0].toUpperCase()}"`;
        if (hintLevel === 3) return `Hint: It starts with "${word[0].toUpperCase()}" and ends with "${word[word.length - 1].toUpperCase()}"`;
        if (hintLevel === 4) { const h = Math.ceil(word.length / 2); return `Hint: ${word.substring(0, h).toUpperCase()}${'_'.repeat(word.length - h)}`; }
        return '';
    };

    const handleNext = () => {
        if (currentWordIndex + 1 < words.length) {
            setCurrentWordIndex(currentWordIndex + 1);
            setUserInput(""); setResult(""); setTries(0); setLocked(false); setStreak(0);
        } else { saveLeaderboard(score, attempts); setShowSummary(true); }
    };

    return (
        <div className="game-container">
            <div className="game-header">
                <span className="game-progress-label">Word {currentWordIndex + 1} / {words.length}</span>
                <button onClick={onExit} className="btn btn-link">
                    <HomeIcon className="icon-sm" /> Exit
                </button>
            </div>

            {/* Wrong Answer Modal */}
            {showWrongAnswer && (
                <div className="modal-backdrop">
                    <div className="game-modal game-modal-wrong">
                        <div className="game-modal-icon wrong"><CloseIcon className="icon-xl" /></div>
                        <div className="game-modal-title">OOPS!</div>
                        <div className="game-modal-message">
                            {tries >= 3 ? 'No more tries!' : `Try again! ${3 - tries} ${3 - tries === 1 ? 'try' : 'tries'} left`}
                        </div>
                        {tries >= 3 && (
                            <>
                                <div className="game-modal-label">The correct spelling is:</div>
                                <div className="game-modal-word">{words[currentWordIndex]?.word}</div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccess && (
                <div className="modal-backdrop">
                    <div className="game-modal game-modal-success">
                        <div className="game-modal-icon success"><CheckIcon className="icon-xl" /></div>
                        <div className="game-modal-title">CORRECT!</div>
                        {scorePopup && <div className="game-modal-points">+{scorePopup.points} pts</div>}
                    </div>
                </div>
            )}

            {scorePopup && (
                <div className="floating-score" style={{ top: `${scorePopup.y}%`, left: `${scorePopup.x}%` }}>
                    +{scorePopup.points}
                </div>
            )}

            {!showSummary && (
                <div className="game-content">
                    {/* Compact stats bar */}
                    <div className="sp-stats-bar">
                        <div className="sp-stat">
                            <LocalFireDepartmentIcon style={{ fontSize: 16, color: '#ef4444' }} />
                            <span>{streak}{streak >= 3 && ` ×${getStreakMultiplier()}`}</span>
                        </div>
                        <div className="sp-stat sp-score">
                            {score} / {words.length}
                        </div>
                        <div className="sp-stat">
                            <DiamondIcon style={{ fontSize: 16, color: '#3b82f6' }} />
                            <span>{totalPoints}</span>
                        </div>
                    </div>

                    {/* Play button */}
                    <button onClick={() => speakWord()} className="sp-play-btn">
                        <VolumeUpIcon style={{ fontSize: 32 }} />
                        <span>Tap to Hear the Word</span>
                    </button>

                    {/* Hint */}
                    {!locked && (
                        <div className="game-hint-section">
                            <button
                                onClick={() => { if (hintLevel < 4) { setHintLevel(hintLevel + 1); setShowHint(true); } }}
                                className={`btn btn-ghost btn-block${hintLevel >= 4 ? ' disabled' : ''}`}
                                disabled={hintLevel >= 4}
                                style={{ fontSize: 13 }}
                            >
                                <TipsAndUpdatesIcon className="icon-sm" />
                                {hintLevel === 0 ? 'Show Hint' : hintLevel >= 4 ? 'No More Hints' : `More Hints (${hintLevel}/4)`}
                            </button>
                            {showHint && hintLevel > 0 && (
                                <div className="game-hint-box">{getHintText()}</div>
                            )}
                        </div>
                    )}

                    {/* Input */}
                    <div className="game-input-section">
                        <input
                            ref={inputRef}
                            type="text"
                            className={`game-input${locked ? ' disabled' : ''}`}
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !locked) { e.preventDefault(); handleSubmit(); } }}
                            disabled={locked}
                            placeholder="Type the word here..."
                            autoComplete="off"
                        />
                    </div>

                    {/* Submit / Next */}
                    {!locked ? (
                        <button onClick={handleSubmit} className="btn btn-success btn-lg btn-block">
                            <CheckIcon className="icon-md" /> Check Answer
                        </button>
                    ) : (
                        <button onClick={handleNext} className="btn btn-primary btn-lg btn-block">
                            <ArrowForwardIcon className="icon-md" /> Next Word
                        </button>
                    )}

                    {result && (
                        <div className={`game-result ${result.includes('Correct') ? 'success' : 'error'}`}>
                            {result}
                        </div>
                    )}

                    <div className="game-spacer" />
                </div>
            )}

            {showSummary && (
                <GameSummary
                    kidName={kidName}
                    totalPoints={totalPoints}
                    personalBest={personalBest}
                    perfectScore={getPerfectScore()}
                    maxStreak={maxStreak}
                    score={score}
                    totalWords={words.length}
                    attempts={attempts}
                    leaderboard={getLeaderboard()}
                />
            )}
        </div>
    );
}
